import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { uniq } from 'lodash';
import {
  useDimensionsLabels,
  useI18n,
  useMainMenuGrouping,
  usePickerPushUrl,
  useTableQuery,
  useUrl,
} from '@/composables';
import { useStore } from '@/store';
import { $emitter } from '@/services/emitter';
import { DrillFilterOptionsEnum } from '@/services/models/Enums';
import query from '@/components/query/query-constructor';

export const useTreeLevels = () => {
  const route = useRoute();
  const store = useStore();
  const { t } = useI18n();
  const { dimensions } = useDimensionsLabels();
  const urlApi = useUrl();
  const tableQueryApi = useTableQuery();
  const { pushReportUrl } = usePickerPushUrl();
  const {
    tabsLayoutFlat,
    tabsPlainTabsDataExclude,
    campaignAsFirstTableFilterCondition,
  } = useMainMenuGrouping();

  const levels = ref<string[]>(['none', 'none']);
  const type = ref('');
  const preserveLevels = ref(false);

  const livesGroup = ['live-visits', 'live-clicks'];
  const otherGroup = ['conversions', 'errors'];

  const drills = computed(() => {
    return tableQueryApi.tableDrilldownFilter.value
      ? tableQueryApi.tableDrilldownFilter.value
        .filter((item: Bemob.ReportQueryDrill) => item.filterOptions === DrillFilterOptionsEnum.equals)
        .map((item: Bemob.ReportQueryDrill) => item.filterTables)
      : [];
  });

  const excludeGroups = computed(() => livesGroup.concat(otherGroup));

  const dimensionKeys = computed(() => {
    const lives = campaignAsFirstTableFilterCondition.value ? livesGroup : [];
    const storeDimensions = store.dimensions as Record<string, { mainColumn?: string; additionalColumns?: string[] }>;

    let result = Object.keys(storeDimensions).concat(otherGroup);

    if (store.user.featureFlags?.reportingFlowRulePath?.enabled) {
      result = result.concat(['flow_rules', 'flow_rule_paths']);
    }

    return result.concat(lives);
  });

  const tableDimensions1 = computed(() => {
    return dimensionKeys.value
      .filter((key) => !tabsPlainTabsDataExclude.value.includes(key))
      .map((dimensionKey) => {
        let text = dimensions.value[dimensionKey as keyof typeof dimensions.value];
        if (dimensionKey.indexOf('custom') === 0) {
          const customKey = dimensionKey + 'Name';
          text = text + (tableQueryApi.tableCustomGroups.value[customKey]
            ? ` (${tableQueryApi.tableCustomGroups.value[customKey]})`
            : '');
        }

        return {
          text,
          id: dimensionKey,
        };
      });
  });

  const tableDimensions2Layer1 = computed(() => {
    const result = tableDimensions1.value.filter((item) => !excludeGroups.value.includes(item.id));
    return [{
      text: t('None'),
      id: 'none',
    }].concat(result);
  });

  const tableDimensions2Layer2 = computed(() => {
    return tableDimensions2Layer1.value.filter((item) => drills.value.indexOf(item.id) === -1);
  });

  const tableDimensions2 = computed(() => {
    return tableDimensions2Layer2.value.filter((item) => item.id !== type.value);
  });

  const tableDimensions3 = computed(() => {
    return tableDimensions2.value.filter((item) => item.id !== levels.value[0]);
  });

  const tableDimensions3Values = computed(() => {
    return tableDimensions3.value.map((item) => item.id);
  });

  const isPickerTree1Hidden = computed(() => {
    return !tableDimensions3Values.value.includes(levels.value[1]);
  });

  const disabled = computed(() => levels.value[0] === 'none' || levels.value.length === 0);

  const exportLevels = (levelValues: string[]) => {
    return levelValues.map((item) => (item === 'none' ? '' : item));
  };

  const importLevels = (levelValues: string[]) => {
    const importedLevels: string[] = [];
    importedLevels[0] = levelValues?.[0] ? levelValues[0] : 'none';
    importedLevels[1] = levelValues?.[1] ? levelValues[1] : 'none';
    return importedLevels;
  };

  const fixLevelProblems = (levelValues: string[]) => {
    let problem = false;
    for (let index = 0; index < 2; index = index + 1) {
      if (!levelValues[index]) {
        levelValues[index] = 'none';
        problem = true;
      }
    }

    if (problem) {
      return true;
    }

    const levelsNotAllowed = levelValues
      .map((levelValue, index) => (urlApi.isHidden(levelValue) || levelValue === type.value ? index : -1))
      .filter((index) => index !== -1);

    if (levelsNotAllowed.length) {
      levelsNotAllowed.forEach((index) => {
        levels.value[index] = 'none';
      });

      if (levels.value[0] === 'none' && levels.value[1] !== 'none') {
        levels.value[0] = levels.value[1];
        levels.value[1] = 'none';
      }

      return true;
    }

    return false;
  };

  const saveLevels = () => {
    const tableTreeLevels = levels.value[0]
      ? uniq(exportLevels(levels.value))
      : null;

    tableQueryApi.setTableTreeLevelsForCurrentTab(tableTreeLevels);
    tableQueryApi.setTableTreeLevelsTemporaryForCurrentTab(tableTreeLevels);
  };

  const levelChanged = (options?: { isPushUrl?: boolean } | string) => {
    const isPushUrl = typeof options === 'object' && options !== null && options.isPushUrl === false
      ? false
      : true;

    tableQueryApi.clearTableSelectedItems();

    const tableTreeLevels = levels.value[0]
      ? uniq(exportLevels(levels.value))
      : null;

    tableQueryApi.setTableTreeLevelsTemporaryForCurrentTab(tableTreeLevels);
    if (tableQueryApi.tableQueryForCurrentTab.value.view === 'tree') {
      saveLevels();
      if (isPushUrl) {
        pushReportUrl();
      }
    }
    $emitter.emit('reset-report-tree');
  };

  const typeChanged = (typeValue: string) => {
    if (typeValue && typeValue !== 'undefined' && typeValue !== 'none' && typeValue !== route.params.type) {
      const tab = tabsLayoutFlat.value[typeValue];
      tab?.click?.({} as MouseEvent);
    }

    fixLevelProblems(levels.value);
  };

  const onBusReportTabUpdated = () => {
    levels.value = tableQueryApi.tableQueryTemporaryForCurrentTab.value.treeLevels
      ? importLevels(tableQueryApi.tableQueryTemporaryForCurrentTab.value.treeLevels as string[])
      : ['none', 'none'];
  };

  const onBusRestoreTreeLevels = (payload: unknown) => {
    const tableTreeLevels = payload as string[];
    const importedTreeLevels = importLevels(tableTreeLevels);

    type.value = importedTreeLevels[0];
    if (tableTreeLevels.length) {
      levels.value = importLevels(tableTreeLevels.slice(1, 3));
      if (levels.value.length < 2) {
        levels.value = levels.value.concat(new Array(2 - levels.value.length).fill('none'));
      }
      preserveLevels.value = true;
    }
  };

  const onBusPreserveLevels = () => {
    preserveLevels.value = true;
  };

  const onBusSpliceLevel = (payload: unknown) => {
    const level = payload as string;
    const filteredLevels = levels.value.filter((item) => item !== level);
    levels.value = levels.value.map((item, index) => (filteredLevels[index] ? filteredLevels[index] : 'none'));

    saveLevels();
  };

  const onBusReportTabAdded = () => {
    type.value = route.params.type as string;
    const decodedUrlParams = query.decode(route.params.data as string);
    if (decodedUrlParams.treeLevels) {
      levels.value = importLevels(decodedUrlParams.treeLevels);
    }
    levelChanged({ isPushUrl: false });
    saveLevels();
  };

  const onBusApplyFilters = () => {
    saveLevels();
    pushReportUrl();
  };

  const clearTableTree = () => {
    $emitter.emit('reset-tree');
    tableQueryApi.setTableTreeLevelsForCurrentTab(null);
    tableQueryApi.resetTableTreeForCurrentTab();
  };

  onMounted(() => {
    $emitter.on('preserve-levels', onBusPreserveLevels);
    $emitter.on('restore-tree-levels', onBusRestoreTreeLevels);
    $emitter.on('splice-level', onBusSpliceLevel);
    $emitter.on('apply-filters', onBusApplyFilters);
    $emitter.on('report-tab-added', onBusReportTabAdded);
    $emitter.on('report-tab-updated', onBusReportTabUpdated);
  });

  onBeforeUnmount(() => {
    $emitter.off('preserve-levels', onBusPreserveLevels);
    $emitter.off('restore-tree-levels', onBusRestoreTreeLevels);
    $emitter.off('splice-level', onBusSpliceLevel);
    $emitter.off('apply-filters', onBusApplyFilters);
    $emitter.off('report-tab-added', onBusReportTabAdded);
    $emitter.off('report-tab-updated', onBusReportTabUpdated);
  });

  watch(type, (typeValue) => {
    typeChanged(typeValue);
  });

  watch(drills, () => {
    nextTick(() => {
      typeChanged(type.value);
    });
  });

  watch(() => route.params.type, (typeValue) => {
    if (preserveLevels.value) {
      preserveLevels.value = false;
    }

    type.value = typeValue as string;
  }, { immediate: true });

  return {
    type,
    levels,
    tableDimensions1,
    tableDimensions2,
    tableDimensions3,
    isPickerTree1Hidden,
    disabled,
    levelChanged,
    clearTableTree,
  };
};
